export const marsTree = {
  id: 'mars',
  label: 'Mars',
  type: 'Terrestrial planet',
  summary: 'The Red Planet: cold, dry, half the size of Earth, with the largest volcano and largest canyon in the solar system.',
  payload: [
    {
      type: 'description',
      text: 'Mars is the fourth planet from the Sun, a cold desert world with thin atmosphere, two small moons, and compelling evidence that liquid water flowed on its surface billions of years ago. Its red color comes from iron oxide (rust) on its surface. Mars hosts Olympus Mons, the largest volcano in the solar system, and Valles Marineris, a canyon system long enough to stretch from California to New York.'
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '6,779 km (0.532 × Earth)' },
        { label: 'Mass',              value: '6.417 × 10²³ kg (0.107 × Earth)' },
        { label: 'Distance from Sun', value: '228.0 M km (1.524 AU)' },
        { label: 'Orbital period',    value: '687 Earth days (1.88 years)' },
        { label: 'Day length',        value: '24h 37m sidereal, 24h 40m solar (a sol)' },
        { label: 'Axial tilt',        value: '25.19° (Earth-like seasons)' },
        { label: 'Moons',             value: '2 (Phobos, Deimos)' },
        { label: 'Surface temp',      value: '−153 to +20 °C' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Physical properties, gravity, magnetic remnants',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '3,396.2 km' },
            { label: 'Mean density',      value: '3.933 g/cm³' },
            { label: 'Surface gravity',   value: '3.73 m/s² (0.38 g)' },
            { label: 'Escape velocity',   value: '5.03 km/s' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Mars once had a global magnetic field, evidenced by magnetized crustal rocks. It shut off ~4 billion years ago. That let the solar wind strip away most of the atmosphere, turning a potentially habitable world into a desert.'
        }
      ]
    },
    {
      id: 'moons',
      label: 'Moons',
      summary: 'Phobos and Deimos, possibly captured asteroids',
      children: [
        {
          id: 'phobos',
          label: 'Phobos',
          summary: 'The larger, doomed inner moon',
          payload: [
            { type: 'description', text: 'Orbits just 6,000 km above Mars\' surface and is spiraling inward. In ~50 million years it will either crash into Mars or tear apart into a ring. Its surface is dominated by the 9 km crater Stickney.' },
            {
              type: 'stats',
              entries: [
                { label: 'Mean diameter', value: '22.2 km' },
                { label: 'Orbital period', value: '7h 39m' },
                { label: 'Density',       value: '1.87 g/cm³' },
                { label: 'Composition',   value: 'Carbonaceous chondrite-like' }
              ]
            }
          ]
        },
        {
          id: 'deimos',
          label: 'Deimos',
          summary: 'The smaller, outer moon',
          payload: [
            { type: 'description', text: 'About half the size of Phobos and two and a half times farther out. From Deimos, Mars spans about 16° of sky, some 30 times wider than the full Moon seen from Earth. Loose dirt fills many of its craters, so it looks smoother than pockmarked Phobos.' },
            {
              type: 'stats',
              entries: [
                { label: 'Mean diameter', value: '12.4 km' },
                { label: 'Orbital period', value: '30h 18m' },
                { label: 'Density',       value: '1.47 g/cm³' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'atmosphere',
      label: 'Atmosphere',
      summary: 'Thin CO₂, dust storms, and seasonal polar CO₂ ice caps',
      payload: [
        {
          type: 'description',
          text: 'Mars\' atmosphere is about 100× thinner than Earth\'s and composed mostly of CO₂. Surface pressure is so low (6 mbar average) that liquid water can\'t last on the surface: it either freezes or boils away. Yet the atmosphere is still thick enough for dust storms that can engulf the entire planet for weeks.'
        },
        {
          type: 'composition-bars',
          entries: [
            { name: 'Carbon dioxide (CO₂)', percent: 95.1, color: '#ff9466' },
            { name: 'Nitrogen (N₂)',        percent: 2.59,  color: '#4a9eff' },
            { name: 'Argon (Ar)',           percent: 1.94,  color: '#c099ff' },
            { name: 'Oxygen (O₂)',          percent: 0.16,  color: '#7dd3a8' },
            { name: 'Carbon monoxide (CO)', percent: 0.06,  color: '#ffd966' },
            { name: 'Water vapor (H₂O)',    percent: 0.021, color: '#88ccff' }
          ]
        }
      ]
    },
    {
      id: 'surface',
      label: 'Surface & geology',
      summary: 'Volcanoes, canyons, craters, polar caps, and ancient water',
      payload: [
        {
          type: 'description',
          text: 'Mars has a striking north-south dichotomy: low, smooth northern plains and ancient, heavily-cratered southern highlands. The Tharsis bulge hosts four enormous shield volcanoes, including Olympus Mons, 21.9 km tall with a base about the size of Arizona. Valles Marineris stretches about 3,900 km and descends as deep as 9 km.'
        },
        {
          type: 'stats',
          label: 'Notable features',
          entries: [
            { label: 'Olympus Mons',    value: '21.9 km tall, largest volcano' },
            { label: 'Valles Marineris', value: '3,900 km long, largest canyon' },
            { label: 'Hellas Basin',    value: '2,300 km wide impact crater' },
            { label: 'Polar caps',      value: 'H₂O + CO₂ ice (seasonal)' },
            { label: 'Recurring slope lineae', value: 'Seasonal dark streaks, likely dry flows' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Evidence for ancient surface water is overwhelming: dried riverbeds, lake-floor sediments, clay minerals, and rounded pebbles studied by Curiosity. Mars had a warmer, wetter past, possibly lasting hundreds of millions of years.'
        }
      ]
    },
    {
      id: 'missions',
      label: 'Missions & exploration',
      summary: 'The most-visited planet besides Earth',
      payload: [
        {
          type: 'stats',
          entries: [
            { label: 'Mariner 4',       value: '1965, first successful flyby' },
            { label: 'Viking 1/2',      value: '1976, first fully successful landers and life tests' },
            { label: 'Sojourner',       value: '1997, first rover' },
            { label: 'Spirit/Opportunity', value: '2004, long-lived twin rovers' },
            { label: 'Curiosity',       value: 'Since 2012, Gale Crater' },
            { label: 'InSight',         value: '2018–2022, seismology & heat flow' },
            { label: 'Perseverance + Ingenuity', value: 'Since 2021, Jezero (Ingenuity to 2024)' },
            { label: 'Tianwen-1',       value: '2021, Chinese orbiter + rover' },
            { label: 'MAVEN',           value: '2014–2025, atmospheric loss (ended 2026)' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Ingenuity, a 1.8 kg helicopter, made the first powered flight on another planet and flew 72 times between April 2021 and January 2024. It exceeded its 5-flight design goal by 14×.'
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA fact sheet',  url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/marsfact.html' },
            { label: 'Mars Exploration', url: 'https://mars.nasa.gov/' },
            { label: 'Perseverance',     url: 'https://mars.nasa.gov/mars2020/' },
            { label: 'NASA: Mars facts', url: 'https://science.nasa.gov/mars/facts/' },
            { label: 'NASA: Viking 1', url: 'https://science.nasa.gov/mission/viking-1/' },
            { label: 'JPL: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
            { label: 'JPL: slope lineae as granular flows', url: 'https://www.jpl.nasa.gov/news/recurring-martian-streaks-flowing-sand-not-water/' },
            { label: 'NASA: MAVEN mission ends', url: 'https://www.nasa.gov/news-release/nasa-says-farewell-to-maven-mars-mission-hosts-media-call-today/' },
            { label: 'Wikipedia: Mars',  url: 'https://en.wikipedia.org/wiki/Mars' }
          ]
        }
      ]
    }
  ]
};
